// Helpers for associating Spyder brands with folders.
//
// A folder's `spyderBrands` array mixes three entry formats:
//   - New Spyder API brand ids, prefixed with 'brand-' (e.g. 'brand-a1b2c3')
//   - Domains, prefixed with 'domain-' (e.g. 'domain-nike.com'). The server files a brand added by
//     domain this way while it's still pending (it has no brand id yet)
//   - Meta ad library page ids, stored bare (e.g. '15087023444'). Used by legacy entries, and by the
//     server for a brand added by ad library page while it's still pending
// The prefix is what tells them apart. Domain entries resolve to a brand by matching one of the
// brand's domains, and page id entries by matching the `advertiser_id` of one of the brand's socials.
// The server swaps a pending brand's entry for 'brand-' once it's built, but that's best effort and
// the cached folders lag behind it, so a built brand still matches its domain / page id entry.
//
// These helpers only read folders. The Spyder API owns writing brand entries (subscribe, unsubscribe
// and moves via PUT /subscriptions/folder), so there's one place that knows how to write them.

import { getBrandAdvertiserIds, getBrandDomains } from './brandUtils'

// Prefix handler methods to account for legacy folder 'spyderBrands' entries.
// New entries are prefixed with 'brand-${postgres_brand_id}'
const BRAND_ID_PREFIX = 'brand-'
const isPrefixedBrandId = (entry) => typeof entry === 'string' && entry.startsWith(BRAND_ID_PREFIX)
const fromPrefixedBrandId = (entry) => isPrefixedBrandId(entry) ? entry.slice(BRAND_ID_PREFIX.length) : entry

// Domain entries are prefixed with 'domain-${domain}'
const DOMAIN_PREFIX = 'domain-'
const isPrefixedDomain = (entry) => typeof entry === 'string' && entry.startsWith(DOMAIN_PREFIX)
const fromPrefixedDomain = (entry) => isPrefixedDomain(entry) ? entry.slice(DOMAIN_PREFIX.length) : entry

// Checks if a brand matches an entry from a folder's 'spyderBrands' array.
// Normalized brands (with `advertiserIds`) carry their domains as strings; raw brands as objects.
const brandMatchesFolderEntry = (brand, entry) => {
  if (!brand || !entry) return false
  if (isPrefixedBrandId(entry)) return !!brand.id && brand.id === fromPrefixedBrandId(entry)
  if (isPrefixedDomain(entry)) {
    const domains = brand.advertiserIds ? (brand.domains || []) : getBrandDomains(brand)
    return domains.includes(fromPrefixedDomain(entry))
  }
  if (brand.advertiserIds) return brand.advertiserIds.includes(entry)
  return getBrandAdvertiserIds(brand).includes(entry)
}

// Given a list of brands (normalized or raw) and a folder, returns the brands that are in the folder.
const getBrandsInFolder = (brands, folder) => {
  return brands.filter(brand => (
    folder?.spyderBrands?.some(entry => brandMatchesFolderEntry(brand, entry)) || false
  ))
}

// Brands that no folder claims — these make up the 'Default Folder'
const getUnfolderedBrands = (brands = [], folders = []) =>
  brands.filter(brand => !findFolderForBrand(folders, brand))

// Given a list of folders and a brand, returns the folder that the brand is in (if any).
const findFolderForBrand = (folders = [], brand) =>
  folders.find(folder => (folder?.spyderBrands || []).some(entry => brandMatchesFolderEntry(brand, entry)))

// .findIndex version of the above
const findFolderIndexForBrand = (folders = [], brand) =>
  folders.findIndex(folder => (folder?.spyderBrands || []).some(entry => brandMatchesFolderEntry(brand, entry)))

// The ref the server moves a brand by (PUT /subscriptions/folder): its brand id, or while it's pending (no brand
// id yet), the advertiser id or domain it was added with. Takes a normalized brand or a raw subscribed brand.
const getBrandFolderRef = (brand) => {
  if (brand?.id) return { brand_id: brand.id }
  const { ref_type: refType, ref } = brand?.brand || {}
  if (!ref) return null
  return refType === 'domain' ? { domain: ref } : { advertiser_id: ref }
}

export {
  BRAND_ID_PREFIX,
  brandMatchesFolderEntry,
  getBrandFolderRef,
  getBrandsInFolder,
  findFolderForBrand,
  findFolderIndexForBrand,
  getUnfolderedBrands
}
